/**
 * 辅助对象管理器
 * 负责网格、坐标轴等辅助对象的创建和管理。
 */
import * as THREE from 'three/webgpu';
import type {Viewer} from '../Viewer';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';

/**
 * 辅助对象管理器配置。
 */
export interface HelperManagerOptions {
    /** 网格尺寸。 */
    gridSize?: number;
    /** 网格分段数。 */
    gridDivisions?: number;
    /** 主网格颜色。 */
    gridColor1?: THREE.ColorRepresentation;
    /** 次网格颜色。 */
    gridColor2?: THREE.ColorRepresentation;
    /** 坐标轴尺寸。 */
    axesSize?: number;
    /** 是否显示网格。 */
    showGrid?: boolean;
}

/**
 * 辅助对象管理器类。
 */
export class HelperManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 网格辅助对象。 */
    private gridHelper: THREE.GridHelper | null = null;
    /** 坐标轴辅助对象。 */
    private axesHelper: THREE.AxesHelper | null = null;
    /** 场景引用。 */
    private readonly scene: THREE.Scene;

    /**
     * @param viewer Viewer 实例。
     * @param options 辅助对象配置。
     */
    constructor(viewer: Viewer, options: HelperManagerOptions = {}) {
        
        // 网格尺寸从原本的 1000 米修正为 1.0 米（1000 mm），坐标轴尺寸从 500 米修正为 0.5 米（500 mm）
        const {
            gridSize = 1.0,
            gridDivisions = 50,
            gridColor1 = 0x444444,
            gridColor2 = 0x222222,
            axesSize = 0.05,
            showGrid = false
        } = options;
        

        this.viewer = viewer;
        this.scene = viewer.scene;
        // 创建网格
        this.gridHelper = new THREE.GridHelper(gridSize, gridDivisions, gridColor1, gridColor2);
        (this.gridHelper as any).ignore = true;
        this.gridHelper.visible = showGrid;
        addObject3DToParent(this.gridHelper, this.scene);

        // 创建坐标轴
        this.axesHelper = new THREE.AxesHelper(axesSize);
        (this.axesHelper as any).ignore = true;
        addObject3DToParent(this.axesHelper, this.scene);
    }

    /**
     * 获取网格辅助对象。
     * @returns 网格辅助对象或 null。
     */
    getGridHelper(): THREE.GridHelper | null {
        return this.gridHelper;
    }

    /**
     * 获取坐标轴辅助对象。
     * @returns 坐标轴辅助对象或 null。
     */
    getAxesHelper(): THREE.AxesHelper | null {
        return this.axesHelper;
    }

    /**
     * 显示网格。
     */
    showGrid(): void {
        if (this.gridHelper) {
            this.gridHelper.visible = true;
        }
    }

    /**
     * 隐藏网格。
     */
    hideGrid(): void {
        if (this.gridHelper) {
            this.gridHelper.visible = false;
        }
    }

    /**
     * 显示坐标轴。
     */
    showAxes(): void {
        if (this.axesHelper) {
            this.axesHelper.visible = true;
        }
    }

    /**
     * 隐藏坐标轴。
     */
    hideAxes(): void {
        if (this.axesHelper) {
            this.axesHelper.visible = false;
        }
    }

    /**
     * 释放辅助对象资源。
     */
    dispose(): void {
        if (this.gridHelper) {
            this.scene.remove(this.gridHelper);
            this.gridHelper.geometry.dispose();
            this.gridHelper.material.dispose();
            this.gridHelper = null;
        }

        if (this.axesHelper) {
            this.scene.remove(this.axesHelper);
            this.axesHelper.geometry.dispose();
            if (this.axesHelper.material instanceof THREE.Material) {
                this.axesHelper.material.dispose();
            } else if (Array.isArray(this.axesHelper.material)) {
                this.axesHelper.material.forEach(m => m.dispose());
            }
            this.axesHelper = null;
        }
    }
}
